// Copyright (c) Brock Allen & Dominick Baier. All rights reserved.
// Licensed under the Apache License, Version 2.0. See LICENSE in the project root for license information.

import { Logger, UrlUtils } from "./utils";
import { ErrorResponse } from "./errors";
import { SSOClientSettings, SSOClientSettingsStore } from "./SSOClientSettings";
import { ResponseValidator } from "./ResponseValidator";
import { MetadataService } from "./MetadataService";
import { SigninRequest } from "./SigninRequest";
import { SigninResponse } from "./SigninResponse";
import { SignoutRequest, SignoutRequestArgs } from "./SignoutRequest";
import { SignoutResponse } from "./SignoutResponse";
import { SigninState } from "./SigninState";
import { State } from "./State";
import { JsonService } from "./JsonService";
import { UserRequest } from "./UserRequest";
import { User } from "./User";
import { UserInfoService } from "./UserInfoService";

/**
 * @public
 */
export interface CreateSigninRequestArgs {
    redirect_uri?: string;

    /** custom "state", which can be used by a caller to have "data" round tripped */
    state?: unknown;

    prompt?: string;
    display?: string;
    max_age?: number;
    ui_locales?: string;
    id_token_hint?: string;
    login_hint?: string;
    acr_values?: string;
    resource?: string;
    response_mode?: "query" | "fragment";
    request?: string;
    request_uri?: string;
    extraQueryParams?: Record<string, string | number | boolean>;
    request_type?: string;
    client_secret?: string;
    extraTokenParams?: Record<string, unknown>;
    skipUserInfo?: boolean;
}

/**
 * @public
 */
export type CreateSignoutRequestArgs = Omit<SignoutRequestArgs, "url" | "state_data"> & { state?: unknown };

export class SSOClient {
    public readonly settings: SSOClientSettingsStore;
    protected readonly _logger = new Logger("SSOClient");
    public readonly metadataService: MetadataService;
    protected readonly _userInfoService: UserInfoService;

    private readonly jsonService: JsonService;

    protected readonly _validator: ResponseValidator;

    protected readonly return_uri_key = "sso_return_uri";

    public constructor(settings: SSOClientSettings) {
        this.settings = new SSOClientSettingsStore(settings);

        this.metadataService = new MetadataService(this.settings);
        this.jsonService = new JsonService();
        this._validator = new ResponseValidator(this.settings, this.metadataService);
        this._userInfoService = new UserInfoService(this.metadataService, this.settings);
    }

    public async createSigninRequest({
        state,
        request,
        request_uri,
        request_type,
        id_token_hint,
        login_hint,
        skipUserInfo,
        redirect_uri = this.settings.redirect_uri,
        prompt = this.settings.prompt,
        display = this.settings.display,
        max_age = this.settings.max_age,
        ui_locales = this.settings.ui_locales,
        acr_values = this.settings.acr_values,
        resource = this.settings.resource,
        response_mode = this.settings.response_mode,
        extraQueryParams = this.settings.extraQueryParams,
        extraTokenParams = this.settings.extraTokenParams,
    }: CreateSigninRequestArgs): Promise<SigninRequest> {
        const logger = this._logger.create("createSigninRequest");

        const url = await this.metadataService.getAuthorizationEndpoint();
        logger.debug("Received authorization endpoint", url);

        const signinRequest = new SigninRequest({
            url,
            authority: this.settings.authority,
            app_id: this.settings.app_id,
            redirect_uri,
            state_data: state,
            prompt, display, max_age, ui_locales, id_token_hint, login_hint, acr_values,
            resource, request, request_uri, extraQueryParams, extraTokenParams, request_type, response_mode,
            app_secret: this.settings.app_secret,
            sealed_app_id: this.settings.sealed_app_id,
            skipUserInfo,
        });
        const signinState = signinRequest.state;
        await this.settings.stateStore.set(signinState.id, signinState.toStorageString());
        await this.settings.stateStore.set(this.return_uri_key, signinRequest.state.data as string);
        return signinRequest;
    }

    public async readSigninResponseState(url: string, removeState = false): Promise<{ state: SigninState; response: SigninResponse }> {
        const logger = this._logger.create("readSigninResponseState");

        const response = new SigninResponse(UrlUtils.readParams(url, this.settings.response_mode));

        if (!response.state) {
            logger.throw(new Error("No state in response"));
            // need to throw within this function's body for type narrowing to work
            throw null; // https://github.com/microsoft/TypeScript/issues/46972
        }

        const storedStateString = await this.settings.stateStore[removeState ? "remove" : "get"](response.state);
        if (!storedStateString) {
            logger.throw(new Error("No matching state found in storage"));
            throw null; // https://github.com/microsoft/TypeScript/issues/46972
        }

        const state = SigninState.fromStorageString(storedStateString);
        return { state, response };
    }


    public async readSigninResponseGlnFedSsoTicket(url: string, removeState = false): Promise<{sso_return_uri:string, response: SigninResponse }> {
        const logger = this._logger.create("readSigninResponseGlnFedSsoTicket");

        const response = new SigninResponse(UrlUtils.readParams(url, this.settings.response_mode));

        if (!response.glnFedSsoTicket) {
            logger.throw(new Error("No glnFedSsoTicket in response"));
            // need to throw within this function's body for type narrowing to work
            throw null; // https://github.com/microsoft/TypeScript/issues/46972
        }

        const sso_return_uri = await this.settings.stateStore[removeState ? "remove" : "get"](this.return_uri_key);

        return { sso_return_uri, response };
    }

    public async processSigninResponse(url: string): Promise<SigninResponse> {
        const logger = this._logger.create("processSigninResponse");

        const { sso_return_uri, response } = await this.readSigninResponseGlnFedSsoTicket(url, true);
        logger.debug("received state from storage; validating response");
        await this._validator.validateSSOSigninResponse(response, this.settings.sealed_app_id);

        response.userState = sso_return_uri;

        return response;
    }

    public async createSignoutRequest({
        state,
        glnFedSsoTicket,
        glnAuthTicket,
        request_type,
        post_logout_redirect_uri = this.settings.post_logout_redirect_uri,
        extraQueryParams = this.settings.extraQueryParams,
    }: CreateSignoutRequestArgs = {}): Promise<SignoutRequest> {
        const logger = this._logger.create("createSignoutRequest");

        let logout_url = await this.metadataService.getEndSessionEndpoint();
        if (!logout_url) {
            logger.throw(new Error("No end session endpoint"));
            throw null; // https://github.com/microsoft/TypeScript/issues/46972
        }

        logger.debug("createSignoutRequest: Received end session endpoint", logout_url);

        const url = await this.jsonService.getLogoutUrl(logout_url, { glnAuthTicket: glnAuthTicket });

        const request = new SignoutRequest({
            url,
            glnFedSsoTicket,
            post_logout_redirect_uri,
            state_data: state,
            extraQueryParams,
            request_type,
        });

        const signoutState = request.state;
        if (signoutState) {
            logger.debug("Signout request has state to persist");
            await this.settings.stateStore.set(signoutState.id, signoutState.toStorageString());
        }

        return request;
    }

    public async readSignoutResponseState(url: string, removeState = false): Promise<{ state: State | undefined; response: SignoutResponse }> {
        const logger = this._logger.create("readSignoutResponseState");

        const response = new SignoutResponse(UrlUtils.readParams(url, this.settings.response_mode));
        if (!response.state) {
            logger.debug("No state in response");

            if (response.error) {
                logger.warn("Response was error:", response.error);
                throw new ErrorResponse(response);
            }

            return { state: undefined, response };
        }

        const storedStateString = await this.settings.stateStore[removeState ? "remove" : "get"](response.state);
        if (!storedStateString) {
            logger.throw(new Error("No matching state found in storage"));
            throw null; // https://github.com/microsoft/TypeScript/issues/46972
        }

        const state = State.fromStorageString(storedStateString);
        return { state, response };
    }

    public async processSignoutResponse(url: string): Promise<SignoutResponse> {
        const logger = this._logger.create("processSignoutResponse");

        const { state, response } = await this.readSignoutResponseState(url, true);
        if (state) {
            logger.debug("Received state from storage; validating response");
            this._validator.validateSignoutResponse(response, state);
        } else {
            logger.debug("No state from storage; skipping response validation");
        }

        return response;
    }

    public clearStaleState(): Promise<void> {
        this._logger.create("clearStaleState");
        return State.clearStaleState(this.settings.stateStore, this.settings.staleStateAgeInSeconds);
    }

    public async getSolaceCredentials(user: User) {
        this._logger.debug("loading solace credentials");

        const credentials = await this._userInfoService.getSolaceCredentials(user.access_token);
        return credentials;

    }
}
